import { getErrorMessage } from "@repo/auth/client";
import { useCallback, useState } from "react";
export function useAuth({
  authClient
}) {
  const [isUpdating, setIsUpdating] = useState(false);
  const signInWithGoogle = useCallback(async () => {
    setIsUpdating(true);
    try {
      const {
        error
      } = await authClient.signIn.social({
        provider: "google",
        callbackURL: `${window.location.origin}`
      });
      if (error) {
        return {
          success: false,
          message: getErrorMessage(error.code)
        };
      }
      return {
        success: true,
        message: "Inloggningen lyckades"
      };
    } finally {
      setIsUpdating(false);
    }
  }, [authClient]);
  const signIn = useCallback(async (email, password) => {
    setIsUpdating(true);
    try {
      const {
        error
      } = await authClient.signIn.email({
        email,
        password
      });
      if (error) {
        if (error.status === 403) {
          return {
            success: false,
            message: "Din e-postadress är inte verifierad. En ny verifieringslänk har skickats till din e-post. Vänligen kontrollera din inkorg och följ instruktionerna för att verifiera din adress."
          };
        }
        return {
          success: false,
          message: getErrorMessage(error.code)
        };
      }
      return {
        success: true,
        message: "Inloggningen lyckades"
      };
    } finally {
      setIsUpdating(false);
    }
  }, [authClient]);
  const signUp = useCallback(async (email_0, password_0, firstName, lastName, redirectTo) => {
    setIsUpdating(true);
    try {
      const {
        error
      } = await authClient.signUp.email({
        email: email_0,
        password: password_0,
        name: `${firstName} ${lastName}`,
        callbackURL: redirectTo
      });
      if (error) {
        return {
          success: false,
          message: getErrorMessage(error.code)
        };
      }
      return {
        success: true,
        message: "Registrering lyckades. En verifieringslänk har skickats till din e-post. Vänligen kontrollera din inkorg och följ instruktionerna för att verifiera din adress."
      };
    } finally {
      setIsUpdating(false);
    }
  }, [authClient]);
  const resendVerification = useCallback(async (email_1, redirectTo_0) => {
    try {
      const {
        error
      } = await authClient.sendVerificationEmail({
        email: email_1,
        ...(redirectTo_0 ? {
          callbackURL: redirectTo_0
        } : {})
      });
      if (error) {
        return {
          success: false,
          message: getErrorMessage(error.code)
        };
      }
      return {
        success: true,
        message: "En ny verifieringslänk är skickad."
      };
    } catch (err) {
      console.error("Failed to resend verification email:", err);
      return {
        success: false,
        message: "Kunde inte skicka länken. Försök igen om en stund."
      };
    }
  }, [authClient]);
  const signOut = useCallback(async () => {
    setIsUpdating(true);
    try {
      await authClient.signOut();
      return {
        success: true,
        message: "Du har loggats ut"
      };
    } catch {
      return {
        success: false,
        message: "Ett tekniskt fel uppstod. Försök igen om en stund."
      };
    } finally {
      setIsUpdating(false);
    }
  }, [authClient]);
  const checkEmailExists = useCallback(async email_2 => {
    try {
      const response = await fetch(`${window.location.origin}/api/auth/check-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          email: email_2
        })
      });
      const data = await response.json();
      if (!response.ok) {
        return {
          exists: false,
          message: "Ett fel uppstod vid kontroll av e-postadressen"
        };
      }
      return {
        exists: data.exists,
        message: ""
      };
    } catch {
      return {
        exists: false,
        message: "Ett fel uppstod vid kontroll av e-postadressen"
      };
    }
  }, []);
  const requestPasswordReset = useCallback(async email_3 => {
    setIsUpdating(true);
    try {
      // Renamed in better-auth 1.5; the old endpoint was removed.
      const {
        error: error_0
      } = await authClient.requestPasswordReset({
        email: email_3,
        redirectTo: `${window.location.origin}/aterstall-losenord`
      });
      if (error_0) {
        return {
          success: false,
          message: getErrorMessage(error_0.code)
        };
      }
      return {
        success: true,
        message: "Om e-postadressen finns i vårt system kommer du att få instruktioner för att återställa ditt lösenord."
      };
    } catch (err_0) {
      console.error("Failed to request password reset:", err_0);
      return {
        success: false,
        message: "Ett okänt fel uppstod. Försök igen om en stund."
      };
    } finally {
      setIsUpdating(false);
    }
  }, [authClient]);
  const resetPassword = useCallback(async (token, newPassword) => {
    if (!token) {
      return {
        success: false,
        message: "Ogiltig eller utgången återställningslänk. Begär en ny återställning av lösenord."
      };
    }
    setIsUpdating(true);
    try {
      const {
        error: error_1
      } = await authClient.resetPassword({
        newPassword,
        token
      });
      if (error_1) {
        if (error_1.status === 422 &&
        // $ERROR_CODES entries are objects since 1.6, not plain strings.
        error_1.code === "INVALID_PASSWORD") {
          return {
            success: false,
            message: "Det nya lösenordet måste vara annorlunda än ditt nuvarande lösenord."
          };
        }
        return {
          success: false,
          message: getErrorMessage(error_1.code)
        };
      }
      return {
        success: true,
        message: "Lösenordet har återställts framgångsrikt"
      };
    } catch (err_1) {
      console.error("Failed to reset password:", err_1);
      return {
        success: false,
        message: "Ett okänt fel uppstod. Försök igen om en stund."
      };
    } finally {
      setIsUpdating(false);
    }
  }, [authClient]);
  return {
    signIn,
    signUp,
    resendVerification,
    signOut,
    checkEmailExists,
    requestPasswordReset,
    resetPassword,
    signInWithGoogle,
    isUpdating
  };
}